/* ============================================================
   Anzeigeformate (nach lilasprojekte lib/format.ts, nur Deutsch — N12).

   Drei Regeln:
   1. Deutsches Dezimalkomma über `Intl.NumberFormat`, nicht über `String.replace`.
   2. Zeiten in Europe/Berlin — nicht in der Zone des Geräts: ein Stand, der um
      23:30 eingefroren wurde, gehört zu DIESEM Tag, auch auf einem Laptop im Urlaub.
   3. Ein unlesbares Datum ergibt "" und niemals „Invalid Date".
   ============================================================ */

const LOCALE = "de-DE";
const ZEITZONE = "Europe/Berlin";

function lesbar(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "06.10.2026" — wie Stände, Bericht und Fassungen. Früher „6. Oktober 2026": neben
 *  „07.10." auf denselben Seiten sah das nach zwei Schreibweisen aus (Prüfung Runde 2). */
export function datum(iso: string | null | undefined): string {
  const d = lesbar(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(LOCALE, { timeZone: ZEITZONE, day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

/** "06.10.2026, 14:05" */
export function datumZeit(iso: string | null | undefined): string {
  const d = lesbar(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: ZEITZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** "Oktober 2026" — die Form des Feldes „Stand" (§3.5: `stand` = 'September 2026'). */
export function standText(jetzt: Date = new Date(), monate?: string[]): string {
  const teile = new Intl.DateTimeFormat(LOCALE, { timeZone: ZEITZONE, month: "numeric", year: "numeric" }).formatToParts(jetzt);
  const monat = Number(teile.find((t) => t.type === "month")?.value ?? "1");
  const jahr = teile.find((t) => t.type === "year")?.value ?? String(jetzt.getFullYear());
  const name = monate?.[monat - 1] ?? new Intl.DateTimeFormat(LOCALE, { timeZone: ZEITZONE, month: "long" }).format(jetzt);
  return `${name} ${jahr}`;
}

/** "vor 3 Min." · "vor 2 Std." · "gestern" · "vor 5 Tagen" · sonst das Datum. */
export function relativZeit(iso: string | null | undefined, jetzt: Date = new Date()): string {
  const d = lesbar(iso);
  if (!d) return "";
  const s = Math.round((jetzt.getTime() - d.getTime()) / 1000);
  if (s < 45) return "gerade eben";
  if (s < 3600) return `vor ${Math.max(1, Math.round(s / 60))} Min.`;
  if (s < 86_400) return `vor ${Math.round(s / 3600)} Std.`;
  const tage = Math.round(s / 86_400);
  if (tage === 1) return "gestern";
  if (tage < 14) return `vor ${tage} Tagen`;
  return datum(iso);
}

/** Sekunden als "0:42" bzw. "1:05" — für den Gegenlauf „Neuen Code in …". */
export function sekundenKurz(s: number): string {
  const ganz = Math.max(0, Math.ceil(s));
  const m = Math.floor(ganz / 60);
  const r = ganz % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Zahl mit deutschem Komma. */
export function zahl(n: number, nachkomma = 0): string {
  return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma }).format(n);
}

/** Dateigröße: "812 kB", "23,4 MB", "1,02 GB" (Basis 1024, wie die Upload-Grenzen §2.4). */
export function bytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${zahl(n / 1024)} kB`;
  if (n < 1024 ** 3) return `${zahl(n / 1024 ** 2, 1)} MB`;
  return `${zahl(n / 1024 ** 3, 2)} GB`;
}
